import React, { useState, useEffect, useCallback } from "react";
import { addProject, getProjects, deleteProject, updateProject } from "./services/projectService";
import { signInAdmin, signOutAdmin, getCurrentUser, sendPasswordReset } from "./services/authService";
import {
  createContentBlock,
  parseImageIndexesInput,
  remapContentBlocksAfterImageRemove,
  remapContentBlocksAfterImageSwap,
  resolveContentBlocks,
  sanitizeContentBlocks
} from "./utils/contentBlocks";

const initialState = {
  name: "",
  type: "commercial",
  description: "",
  location: "",
  year: "",
  team: "",
  client: "",
  status: "Completed",
  isFeatured: false,
  mainImage: null,
  mainImagePreview: null,
  images: [],
  imagesPreview: [],
  contentBlocks: [],
  testimonialText: "",
  testimonialAuthor: "",
  spacing: ""
};

export default function AddProject() {
  const [form, setForm] = useState(initialState);
  const [activeMode, setActiveMode] = useState('edit'); // 'edit', 'preview', or 'manage'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [imageOrder, setImageOrder] = useState([]);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);

  // Function to load projects (memoized with useCallback)
  const loadProjects = useCallback(async () => {
    setProjectsLoading(true);
    setProjectsError(null);
    try {
      const allProjects = await getProjects(true);
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjectsError('Failed to load projects. Please try again.');
      showToast('Failed to load projects', 'error');
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // Handle edit project
  const handleEditProject = (project) => {
    const dbImages = project.images || [];
    setForm((prev) => ({
      ...initialState,
      ...project,
      isFeatured: !!project.isFeatured,
      testimonialText: project?.testimonial?.text || project?.testimonialText || '',
      testimonialAuthor: project?.testimonial?.author || project?.testimonialAuthor || '',
      contentBlocks: sanitizeContentBlocks(project.contentBlocks),
      // If user already selected File objects, preserve them
      mainImage: prev.mainImage instanceof File ? prev.mainImage : project.mainImage,
      images: Array.isArray(prev.images) && prev.images.length && prev.images[0] instanceof File ? prev.images : dbImages,
      mainImagePreview: prev.mainImagePreview || project.mainImage,
      imagesPreview: prev.imagesPreview && prev.imagesPreview.length ? prev.imagesPreview : dbImages
    }));
    // Initialize image order for database images too
    setImageOrder(dbImages.map((_, idx) => idx));
    setEditingProjectId(project.id);
    setActiveMode('edit');
  };

  const swapImages = (firstIndex, secondIndex) => {
    if (firstIndex === secondIndex) return;

    setForm((prev) => {
      const nextImagesPreview = [...prev.imagesPreview];
      const nextImages = [...prev.images];

      [nextImagesPreview[firstIndex], nextImagesPreview[secondIndex]] = [nextImagesPreview[secondIndex], nextImagesPreview[firstIndex]];
      [nextImages[firstIndex], nextImages[secondIndex]] = [nextImages[secondIndex], nextImages[firstIndex]];

      return {
        ...prev,
        imagesPreview: nextImagesPreview,
        images: nextImages,
        contentBlocks: remapContentBlocksAfterImageSwap(prev.contentBlocks, firstIndex, secondIndex)
      };
    });

    setImageOrder((prevOrder) => {
      const nextOrder = [...prevOrder];
      [nextOrder[firstIndex], nextOrder[secondIndex]] = [nextOrder[secondIndex], nextOrder[firstIndex]];
      return nextOrder;
    });
  };

  // Move image up in the sequence
  const moveImageUp = (index) => {
    if (index > 0) {
      swapImages(index, index - 1);
    }
  };

  // Move image down in the sequence
  const moveImageDown = (index) => {
    if (index < imageOrder.length - 1) {
      swapImages(index, index + 1);
    }
  };

  // Remove image from sequence
  const removeImage = (index) => {
    setForm((prev) => {
      const nextImages = prev.images.filter((_, i) => i !== index);
      const nextImagesPreview = prev.imagesPreview.filter((_, i) => i !== index);

      return {
        ...prev,
        imagesPreview: nextImagesPreview,
        images: nextImages,
        contentBlocks: remapContentBlocksAfterImageRemove(prev.contentBlocks, index)
      };
    });

    const newOrder = imageOrder.filter(i => i !== index).map(i => i > index ? i - 1 : i);
    setImageOrder(newOrder);
  };

  // Handle drag start
  const handleDragStart = (index) => {
    setDraggedImageIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // Handle drop
  const handleDrop = (dropIndex) => {
    if (draggedImageIndex !== null && draggedImageIndex !== dropIndex) {
      swapImages(draggedImageIndex, dropIndex);
      setDraggedImageIndex(null);
    }
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedImageIndex(null);
  };

  const addContentLayoutBlock = (type) => {
    setForm((prev) => ({
      ...prev,
      contentBlocks: [...sanitizeContentBlocks(prev.contentBlocks), createContentBlock(type, prev.imagesPreview.length)]
    }));
  };

  const updateContentBlock = (blockId, updates) => {
    setForm((prev) => ({
      ...prev,
      contentBlocks: sanitizeContentBlocks(prev.contentBlocks).map((block) => (
        block.id === blockId ? { ...block, ...updates } : block
      ))
    }));
  };

  const removeContentBlock = (blockId) => {
    setForm((prev) => ({
      ...prev,
      contentBlocks: sanitizeContentBlocks(prev.contentBlocks).filter((block) => block.id !== blockId)
    }));
  };

  const moveContentBlock = (blockIndex, direction) => {
    setForm((prev) => {
      const blocks = [...sanitizeContentBlocks(prev.contentBlocks)];
      const targetIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;

      if (targetIndex < 0 || targetIndex >= blocks.length) {
        return prev;
      }

      [blocks[blockIndex], blocks[targetIndex]] = [blocks[targetIndex], blocks[blockIndex]];
      return {
        ...prev,
        contentBlocks: blocks
      };
    });
  };

  const updateGalleryIndexes = (blockId, value) => {
    updateContentBlock(blockId, { imageIndexes: parseImageIndexesInput(value) });
  };

  // Handle delete project
  const handleDeleteProject = (projectId) => {
    if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      setProjectsLoading(true);
      deleteProject(projectId)
        .then(() => {
          showToast('Project deleted successfully', 'success');
          loadProjects(); // Refresh the list
        })
        .catch((error) => {
          console.error('Error deleting project:', error);
          showToast('Failed to delete project. Please try again.', 'error');
        })
        .finally(() => {
          setProjectsLoading(false);
        });
    }
  };

  // Handle publish project
  const handlePublishProject = async (projectId) => {
    if (window.confirm('Are you sure you want to publish this draft? It will be visible to the public.')) {
      setProjectsLoading(true);
      try {
        await updateProject(projectId, { status: 'Completed' });
        showToast('Project published successfully', 'success');
        loadProjects(); // Refresh the list
      } catch (error) {
        console.error('Error publishing project:', error);
        showToast('Failed to publish project. Please try again.', 'error');
      } finally {
        setProjectsLoading(false);
      }
    }
  };

  // Load projects when entering manage mode
  useEffect(() => {
    if (activeMode === 'manage') {
      loadProjects();
    }
  }, [activeMode, loadProjects]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);

  function showToast(message, type = "info") {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 4000);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    
    try {
      const user = await signInAdmin(loginEmail, loginPassword);
      setUser(user);
      setShowLogin(false);
      setLoginEmail("");
      setLoginPassword("");
    } catch (error) {
      setLoginError(error.message);
    }
  }

  async function handleLogout() {
    await signOutAdmin();
    setUser(null);
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    try {
      await sendPasswordReset(resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setResetEmail('');
      // Auto-hide the forgot password form after successful send
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetMessage('');
      }, 3000);
    } catch (error) {
      setResetError(error.message);
    } finally {
      setResetLoading(false);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }

  function handleMainImageChange(e) {
    const file = e.target.files[0];
    if (file) {
      setForm((prev) => ({ ...prev, mainImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ ...prev, mainImagePreview: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  }

  function handleImagesChange(e) {
    const files = Array.from(e.target.files);
    setForm((prev) => ({ ...prev, images: files, contentBlocks: sanitizeContentBlocks(prev.contentBlocks) }));
    // Initialize image order
    setImageOrder(files.map((_, idx) => idx));
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(file);
          })
      )
    ).then((previews) => {
      setForm((prev) => ({ ...prev, imagesPreview: previews, contentBlocks: sanitizeContentBlocks(prev.contentBlocks) }));
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    handleFirebaseSubmit();
  }

  async function handleSaveAsDraft() {
    const draftForm = { ...form, status: 'Draft' };
    await handleFirebaseSubmit(draftForm);
  }

  async function handleFirebaseSubmit(projectData = form) {
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!projectData.name || !projectData.description) {
        showToast("Please fill in all required fields (Name, Description)", "error");
        setIsSubmitting(false);
        return;
      }

      // Validate main image
      if (!projectData.mainImagePreview) {
        showToast("Please upload a main image for the project", "error");
        setIsSubmitting(false);
        return;
      }

      // Remove preview fields before sending to Firestore
      const { imagesPreview, mainImagePreview, ...cleanData } = projectData;
      
      // Ensure images are in the reordered sequence
      // If images are from database (strings), keep them as is
      // If images are File objects, they'll be handled by Firebase service
      if (cleanData.images && cleanData.images.length > 0) {
        cleanData.images = cleanData.images.map(image => image);
      }

      cleanData.contentBlocks = sanitizeContentBlocks(cleanData.contentBlocks);

      // Submit to Firebase
      if (editingProjectId) {
        // Update existing project
        await updateProject(editingProjectId, cleanData);
        showToast(`Project "${projectData.name}" updated successfully!`, "success");
        console.log('Project updated with ID:', editingProjectId);
      } else {
        // Add new project
        const result = await addProject(cleanData);
        console.log('Project added with ID:', result.id);
      }

  showToast(`Project "${projectData.name}" ${editingProjectId ? 'updated' : projectData.status === 'Draft' ? 'saved as draft' : 'submitted'} successfully!`, "success");

      // Reset form and mode
      setForm(initialState);
      setEditingProjectId(null);
      setImageOrder([]);
      setActiveMode('edit');

    } catch (error) {
      console.error('Error submitting project:', error);
      showToast('Error submitting project. Please try again.', "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  function renderPreview() {
    const images = form.imagesPreview || [];
    const resolvedBlocks = resolveContentBlocks(form.contentBlocks, images);
    const hasContentBlocks = resolvedBlocks.length > 0;

    const getBlockSequence = (block) => {
      if (block?.sequence === 'before-details' || block?.sequence === 'after-details' || block?.sequence === 'after-images') {
        return block.sequence;
      }

      return null;
    };

    const hasSequencedBlocks = hasContentBlocks;

    const renderPreviewImage = (imageSrc, index) => {
      if (!imageSrc) return null;

      return (
        <div style={{ aspectRatio: "4 / 3", minHeight: "220px", maxHeight: "445px", overflow: "hidden", borderRadius: 6 }}>
          <img
            src={imageSrc}
            alt={`Gallery ${index + 1}`}
            style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
          />
        </div>
      );
    };

    const renderLegacyDetailsSection = (needsBottomSpacing = images.length > 0) => (
      <div style={{ marginBottom: needsBottomSpacing ? "32px" : 0 }}>
        <div style={{ color: "#baa170", fontSize: "0.85rem", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px", fontWeight: 600 }}>
          Project Details
        </div>
        <div style={{ color: "#181816", fontSize: "1.1rem", lineHeight: "1.6", whiteSpace: "pre-wrap", maxWidth: "760px" }}>
          {form.description}
        </div>
      </div>
    );

    const renderLegacyGallerySection = (needsBottomSpacing = false) => (
      images.length > 0 && (
        <div style={{
          marginBottom: needsBottomSpacing ? 32 : 0,
          display: "grid",
          gridTemplateColumns: windowWidth <= 768 ? "1fr" : "1fr 1fr",
          gap: windowWidth <= 600 ? 12 : windowWidth <= 768 ? 16 : 24
        }}>
          {images.map((imageSrc, idx) => (
            <div key={`legacy-${idx}`}>
              {renderPreviewImage(imageSrc, idx)}
            </div>
          ))}
        </div>
      )
    );

    const renderLegacyDetails = () => (
      <>
        {renderLegacyDetailsSection()}
        {renderLegacyGallerySection()}
      </>
    );

    const renderImageTextLayout = (block, key, showLabel = false) => {
      const textPart = (
        <div style={{ width: "100%", minWidth: 0 }}>
          {showLabel && (
            <div style={{ color: "#baa170", fontSize: "0.85rem", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px", fontWeight: 600 }}>
              Project Details
            </div>
          )}
          <div style={{ color: "#181816", fontSize: "1.1rem", lineHeight: "1.6", whiteSpace: "pre-wrap", maxWidth: "760px" }}>
            {block.text}
          </div>
        </div>
      );

      const imagePart = renderPreviewImage(block.imageSrc, block.imageIndex || 0);
      const stackedLayout = block.placement === 'stacked' || windowWidth <= 900;
      const imageFirst = block.placement === 'image-left';

      if (stackedLayout) {
        return (
          <div key={key} style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginBottom: 32 }}>
            {imageFirst ? imagePart : textPart}
            {imageFirst ? textPart : imagePart}
          </div>
        );
      }

      return (
        <div key={key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 36, alignItems: "flex-start", marginBottom: 32 }}>
          {imageFirst ? imagePart : textPart}
          {imageFirst ? textPart : imagePart}
        </div>
      );
    };

    const renderSequencedBlock = (block, key) => {
      if (block.type === 'text') {
        return (
          <div key={key} style={{ marginBottom: 32 }}>
            <div style={{ color: "#181816", fontSize: "1.1rem", lineHeight: "1.6", whiteSpace: "pre-wrap", maxWidth: "760px" }}>
              {block.text}
            </div>
          </div>
        );
      }

      if (block.type === 'image') {
        return (
          <div key={key} style={{ marginBottom: 32 }}>
            {renderPreviewImage(block.imageSrc, block.imageIndex || 0)}
          </div>
        );
      }

      if (block.type === 'imageText') {
        return renderImageTextLayout(block, key, false);
      }

      if (block.type === 'gallery') {
        return (
          <div key={key} style={{
            display: "grid",
            gridTemplateColumns: windowWidth <= 768 ? "1fr" : "1fr 1fr",
            gap: windowWidth <= 600 ? 12 : windowWidth <= 768 ? 16 : 24,
            marginBottom: 32
          }}>
            {block.galleryImages.map((item) => (
              <div key={`${key}-${item.index}`}>
                {renderPreviewImage(item.src, item.index)}
              </div>
            ))}
          </div>
        );
      }

      return null;
    };

    const renderSequencedFlowWithStatic = () => {
      const beforeDetailsBlocks = [];
      const afterDetailsBlocks = [];
      const afterImagesBlocks = [];

      resolvedBlocks.forEach((block, idx) => {
        const sequence = getBlockSequence(block) || 'after-details';
        const key = `${block.id || `${block.type}-${idx}`}-sequenced-${idx}`;

        if (sequence === 'before-details') {
          beforeDetailsBlocks.push({ block, key });
          return;
        }

        if (sequence === 'after-images') {
          afterImagesBlocks.push({ block, key });
          return;
        }

        afterDetailsBlocks.push({ block, key });
      });

      return (
        <>
          {beforeDetailsBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
          {renderLegacyDetailsSection(afterDetailsBlocks.length > 0 || images.length > 0)}
          {afterDetailsBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
          {renderLegacyGallerySection(afterImagesBlocks.length > 0)}
          {afterImagesBlocks.map(({ block, key }) => renderSequencedBlock(block, key))}
        </>
      );
    };

    const renderBlockDetails = () => {
      let detailsLabelShown = false;

      return resolvedBlocks.map((block, idx) => {
        const key = block.id || `${block.type}-${idx}`;

        if (block.type === 'text') {
          const showLabel = !detailsLabelShown;
          if (showLabel) detailsLabelShown = true;

          return (
            <div key={key} style={{ marginBottom: 32 }}>
              {showLabel && (
                <div style={{ color: "#baa170", fontSize: "0.85rem", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px", fontWeight: 600 }}>
                  Project Details
                </div>
              )}
              <div style={{ color: "#181816", fontSize: "1.1rem", lineHeight: "1.6", whiteSpace: "pre-wrap", maxWidth: "760px" }}>
                {block.text}
              </div>
            </div>
          );
        }

        if (block.type === 'image') {
          return (
            <div key={key} style={{ marginBottom: 32 }}>
              {renderPreviewImage(block.imageSrc, block.imageIndex || 0)}
            </div>
          );
        }

        if (block.type === 'imageText') {
          const showLabel = !detailsLabelShown;
          if (showLabel) detailsLabelShown = true;

          return renderImageTextLayout(block, key, showLabel);
        }

        if (block.type === 'gallery') {
          return (
            <div key={key} style={{
              display: "grid",
              gridTemplateColumns: windowWidth <= 768 ? "1fr" : "1fr 1fr",
              gap: windowWidth <= 600 ? 12 : windowWidth <= 768 ? 16 : 24,
              marginBottom: 32
            }}>
              {block.galleryImages.map((item) => (
                <div key={`${key}-${item.index}`}>
                  {renderPreviewImage(item.src, item.index)}
                </div>
              ))}
            </div>
          );
        }

        return null;
      });
    };

    return (
      <div style={{ background: "#fff", minHeight: "100vh", color: "#181816" }}>
        {/* Preview indicator */}
        <div style={{ 
          position: "fixed", 
          top: 20, 
          left: 20, 
          background: "rgba(186,161,112,0.95)", 
          color: "#fff", 
          padding: "10px 16px", 
          borderRadius: 6, 
          fontSize: "0.85rem", 
          fontWeight: 600, 
          zIndex: 1000,
        }}>
          📱 Live Preview - Matches Actual Display
        </div>
        <div style={{ width: "100%", background: "#fff" }}>
          {/* Project title and metadata at top */}
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <div style={{ width: "94vw", maxWidth: 1280, margin: "40px auto 0" }}>
              <div style={{
                display: "flex",
                flexDirection: windowWidth <= 768 ? "column" : "row",
                justifyContent: "space-between",
                alignItems: windowWidth <= 768 ? "stretch" : "flex-start",
                gap: windowWidth <= 768 ? "14px" : 0,
                width: "100%"
              }}>
                {/* Project title left */}
                <div style={{ textAlign: "left", flex: windowWidth <= 768 ? "1 1 100%" : "0 1 auto", minWidth: 0 }}>
                  <h2 style={{
                    color: "#baa170",
                    fontFamily: 'instrument serif, serif',
                    fontWeight: 300,
                    fontSize: windowWidth <= 480 ? "1.75rem" : windowWidth <= 768 ? "2rem" : "2.4rem",
                    letterSpacing: "-1px",
                    lineHeight: 1.15,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    margin: 0
                  }}>
                    {form.name || "Project Title"}
                  </h2>
                </div>
                {/* Metadata right */}
                <div style={{
                  textAlign: "left",
                  color: "#baa170",
                  minWidth: windowWidth <= 768 ? 0 : 320,
                  width: windowWidth <= 768 ? "100%" : "auto",
                  borderLeft: windowWidth <= 768 ? "none" : "1.5px solid #baa170",
                  borderTop: windowWidth <= 768 ? "1.5px solid #baa170" : "none",
                  paddingLeft: windowWidth <= 768 ? 0 : "25px",
                  paddingTop: windowWidth <= 768 ? "12px" : 0
                }}>
                  {form.type && (
                    <div style={{ fontSize: "1rem", marginBottom: "16px" }}>
                      <span style={{ color: "#baa170", fontWeight: 600 }}>Type:</span> {form.type.charAt(0).toUpperCase() + form.type.slice(1)}
                    </div>
                  )}
                  {form.client && (
                    <div style={{ fontSize: "1rem", marginBottom: "16px" }}>
                      <span style={{ color: "#baa170", fontWeight: 600 }}>Client:</span> {form.client}
                    </div>
                  )}
                  {form.location && (
                    <div style={{ fontSize: "1rem", marginBottom: "16px" }}>
                      <span style={{ color: "#baa170", fontWeight: 600 }}>Location:</span> {form.location}
                    </div>
                  )}
                  {form.year && (
                    <div style={{ fontSize: "1rem", marginBottom: "16px" }}>
                      <span style={{ color: "#baa170", fontWeight: 600 }}>Year:</span> {form.year}
                    </div>
                  )}
                  {form.team && (
                    <div style={{ fontSize: "1rem" }}>
                      <span style={{ color: "#baa170", fontWeight: 600 }}>Team:</span> {form.team}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Hero image */}
          <div style={{ width: "94vw", maxWidth: 1280, margin: "24px auto 0" }}>
            <div style={{ overflow: "hidden", borderRadius: 6 }}>
              {form.mainImagePreview ? (
                <img 
                  src={form.mainImagePreview} 
                  alt={form.name || "Project Preview"} 
                  style={{ 
                    width: "100%", 
                    height: windowWidth <= 480 ? "200px" : windowWidth <= 768 ? "300px" : "500px",
                    objectFit: "cover", 
                    display: "block", 
                    borderRadius: 6 
                  }} 
                />
              ) : (
                <div style={{ 
                  width: "100%", 
                  height: windowWidth <= 480 ? "200px" : windowWidth <= 768 ? "300px" : "500px",
                  background: "#f5f5f5", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  borderRadius: 6,
                  color: "#baa170",
                  fontSize: "1.1rem"
                }}>
                  No main image selected
                </div>
              )}
            </div>
          </div>

          {/* Content blocks (new system) or fallback details + gallery */}
          <div style={{ width: "94vw", maxWidth: 1280, margin: "50px auto 50px auto", padding: "0 40px", boxSizing: "border-box" }}>
            {hasSequencedBlocks ? renderSequencedFlowWithStatic() : hasContentBlocks ? renderBlockDetails() : renderLegacyDetails()}
          </div>

          {/* Testimonial Section */}
          {form.testimonialText && form.testimonialAuthor && (
            <div style={{ width: "94vw", maxWidth: 1280, margin: "60px auto 80px auto", padding: "0 40px", boxSizing: "border-box" }}>
              <div style={{ display: "flex", gap: "60px", alignItems: "flex-start" }}>
                <div style={{ flex: "0 0 300px" }}>
                  <div style={{ color: "#baa170", fontSize: "0.85rem", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "16px", fontWeight: 600 }}>
                    Testimonial
                  </div>
                </div>
                <div style={{ flex: "1" }}>
                  <div style={{ color: "#181816", fontSize: "1.1rem", lineHeight: "1.8", marginBottom: "24px", fontStyle: "italic" }}>
                    "{form.testimonialText}"
                  </div>
                  <div style={{ color: "#baa170", fontSize: "0.95rem", fontWeight: 600 }}>
                    {form.testimonialAuthor}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="add-project-page" style={{ background: "#fff", color: "#baa170", minHeight: "100vh", padding: "48px 0", fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <h1 style={{ textAlign: "center", fontSize: "2.5rem", marginBottom: 32, fontWeight: 700, letterSpacing: 1, color: "#baa170" }}>{editingProjectId ? 'Edit Project' : 'Add New Project'}</h1>
      
      {/* Toast Notification */}
      {toast.show && (
        <div style={{
          position: "fixed",
          top: 20,
          right: 20,
          background: toast.type === "success" ? "#4CAF50" : toast.type === "error" ? "#f44336" : "#2196F3",
          color: "white",
          padding: "16px 24px",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          zIndex: 1000,
          maxWidth: 400,
          fontSize: "0.95rem",
          fontWeight: 500
        }}>
          {toast.message}
        </div>
      )}
      
      {/* Authentication section */}
      {!user ? (
        <div style={{ maxWidth: 400, margin: "0 auto 48px", background: "#fff", padding: 32, borderRadius: 12, border: "1px solid #baa170" }}>
          <h2 style={{ textAlign: "center", marginBottom: 24, color: "#baa170" }}>Admin Login Required</h2>
          {!showLogin ? (
            <div style={{ textAlign: "center" }}>
              <p style={{ marginBottom: 20, color: "#baa170" }}>You need to be logged in as an admin to add projects.</p>
              <button 
                onClick={() => setShowLogin(true)}
                style={{ 
                  background: "#baa170", 
                  color: "#fff", 
                  border: "none", 
                  borderRadius: 8, 
                  padding: "12px 24px", 
                  fontSize: "1rem", 
                  fontWeight: 600, 
                  cursor: "pointer" 
                }}
              >
                Login as Admin
              </button>
            </div>
          ) : (
            <form onSubmit={handleLogin}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#baa170" }}>Email</label>
                <input 
                  type="email" 
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  required
                  style={{ 
                    width: "100%", 
                    padding: 10, 
                    borderRadius: 6, 
                    border: "1px solid #baa170", 
                    background: "#fff", 
                    color: "#181816" 
                  }} 
                />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", marginBottom: 8, color: "#baa170" }}>Password</label>
                <input 
                  type="password" 
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  required
                  style={{ 
                    width: "100%", 
                    padding: 10, 
                    borderRadius: 6, 
                    border: "1px solid #baa170", 
                    background: "#fff", 
                    color: "#181816" 
                  }} 
                />
              </div>
              {loginError && (
                <div style={{ color: "#f44336", marginBottom: 16, fontSize: "0.9rem" }}>
                  {loginError}
                </div>
              )}
              <div style={{ display: "flex", gap: 12 }}>
                <button 
                  type="button"
                  onClick={() => setShowLogin(false)}
                  style={{ 
                    flex: 1,
                    background: "transparent", 
                    color: "#baa170", 
                    border: "1px solid #baa170", 
                    borderRadius: 6, 
                    padding: "10px", 
                    cursor: "pointer" 
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  style={{ 
                    flex: 1,
                    background: "#baa170", 
                    color: "#fff", 
                    border: "none", 
                    borderRadius: 6, 
                    padding: "10px", 
                    fontWeight: 600, 
                    cursor: "pointer" 
                  }}
                >
                  Login
                </button>
              </div>
              
              <div style={{ textAlign: "center", marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(!showForgotPassword)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#baa170",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    textDecoration: "underline"
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          )}
          
          {/* Forgot Password Form */}
          {showForgotPassword && (
            <div style={{ 
              background: "#fff", 
              padding: 24, 
              borderRadius: 12,
              border: "1px solid #baa170",
              marginTop: 16
            }}>
              <h4 style={{ marginBottom: 16, textAlign: "center", color: "#baa170" }}>
                Reset Password
              </h4>
              
              {resetMessage && (
                <div style={{ 
                  background: "#4CAF50", 
                  color: "white", 
                  padding: 10, 
                  borderRadius: 6, 
                  marginBottom: 16, 
                  textAlign: "center",
                  fontSize: "0.9rem"
                }}>
                  {resetMessage}
                </div>
              )}
              
              {resetError && (
                <div style={{ 
                  background: "#f44336", 
                  color: "white", 
                  padding: 10, 
                  borderRadius: 6, 
                  marginBottom: 16, 
                  textAlign: "center",
                  fontSize: "0.9rem"
                }}>
                  {resetError}
                </div>
              )}
              
              <form onSubmit={handleForgotPassword}>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", marginBottom: 8, color: "#baa170", fontSize: "0.9rem" }}>
                    Admin Email
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="btwpune@gmail.com"
                    required
                    style={{ 
                      width: "100%", 
                      padding: 10, 
                      borderRadius: 6, 
                      border: "1px solid #baa170", 
                      background: "#fff", 
                      color: "#181816",
                      fontSize: "0.9rem"
                    }}
                  />
                </div>
                
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    type="submit"
                    disabled={resetLoading}
                    style={{ 
                      flex: 1,
                      background: resetLoading ? "#ccc" : "#F7F5E6", 
                      color: "#181816", 
                      border: "none", 
                      borderRadius: 6, 
                      padding: "10px", 
                      fontSize: "0.9rem", 
                      fontWeight: 600, 
                      cursor: resetLoading ? "not-allowed" : "pointer"
                    }}
                  >
                    {resetLoading ? "Sending..." : "Send Reset"}
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                      setResetError('');
                      setResetMessage('');
                    }}
                    style={{ 
                      flex: 1,
                      background: "transparent", 
                      color: "#B8B7AA", 
                      border: "1px solid #35332c", 
                      borderRadius: 6, 
                      padding: "10px", 
                      fontSize: "0.9rem", 
                      cursor: "pointer"
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <p style={{ color: "#181618", marginBottom: 12 }}>
            Logged in as: <strong>{user.email}</strong>
          </p>
          <button 
            onClick={handleLogout}
            style={{ 
              background: "transparent", 
              color: "#181618", 
              border: "1px solid #baa170", 
              borderRadius: 6, 
              padding: "8px 16px", 
              fontSize: "0.9rem", 
              cursor: "pointer" 
            }}
          >
            Logout
          </button>
        </div>
      )}
      
      {/* Only show form controls and content if user is logged in */}
      {user && (
        <>
          {/* Toggle buttons */}
          <div style={{ textAlign: "center", marginBottom: 32, display: 'flex', justifyContent: 'center', gap: 0 }}>
            <button 
              type="button"
              onClick={() => setActiveMode('edit')}
              style={{ 
                background: activeMode === 'edit' ? "#baa170" : "transparent", 
                color: activeMode === 'edit' ? "#fff" : "#baa170", 
                border: "1px solid #baa170", 
                borderRadius: "8px 0 0 8px", 
                padding: "12px 24px", 
                fontSize: "1rem", 
                fontWeight: 600, 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Edit
            </button>
            <button 
              type="button"
              onClick={() => setActiveMode('preview')}
              style={{ 
                background: activeMode === 'preview' ? "#baa170" : "transparent", 
                color: activeMode === 'preview' ? "#fff" : "#baa170", 
                border: "1px solid #baa170", 
                borderLeft: "1px solid #baa170",
                borderRadius: 0, 
                padding: "12px 24px", 
                fontSize: "1rem", 
                fontWeight: 600, 
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Preview
            </button>
            <button
              type="button"
              onClick={() => setActiveMode('manage')}
              style={{
                background: activeMode === 'manage' ? "#baa170" : "transparent",
                color: activeMode === 'manage' ? "#fff" : "#baa170",
                border: "1px solid #baa170",
                borderLeft: "1px solid #baa170",
                borderRadius: "0 8px 8px 0",
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              Manage Project
            </button>
          </div>

          {activeMode === 'manage' && (
            <div style={{ 
              marginTop: "32px", 
              padding: "0 20px"
            }}>
              <h2 style={{ 
                fontSize: "2rem", 
                marginBottom: "24px", 
                textAlign: "center",
                color: "#baa170" 
              }}>
                All Projects
              </h2>
              
              {projectsLoading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ color: "#baa170" }}>Loading projects...</div>
                </div>
              ) : projectsError ? (
                <div style={{ 
                  textAlign: "center", 
                  padding: "20px", 
                  color: "#f44336",
                  background: "rgba(244, 67, 54, 0.1)",
                  borderRadius: "8px",
                  margin: "20px auto",
                  maxWidth: "600px"
                }}>
                  {projectsError}
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: "32px",
                  maxWidth: "1200px",
                  margin: "0 auto"
                }}>
                  {projects.map((project) => (
                    <div key={project.id} style={{
                      background: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      transition: "transform 0.2s",
                      position: "relative",
                      border: "1px solid #baa170"
                    }}>
                      <div style={{
                        height: "200px",
                        overflow: "hidden",
                        position: "relative"
                      }}>
                        <img 
                          src={project.mainImage} 
                          alt={project.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                          }}
                        />
                      </div>
                      <div style={{ padding: "20px" }}>
                        <h3 style={{ 
                          fontSize: "1.25rem",
                          fontWeight: "600",
                          marginBottom: "8px",
                          color: "#baa170"
                        }}>
                          {project.name}
                        </h3>
                        <p style={{ 
                          fontSize: "0.9rem",
                          color: "#181618",
                          textTransform: "uppercase",
                          marginBottom: "8px"
                        }}>
                          [{project.type}]
                        </p>
                        <p style={{ 
                          fontSize: "0.8rem",
                          color: project.status === 'Draft' ? "#FFC107" : "#4CAF50",
                          fontWeight: "600",
                          marginBottom: "16px"
                        }}>
                          Status: {project.status}
                        </p>
                        <p style={{
                          fontSize: "0.8rem",
                          color: project.isFeatured ? "#baa170" : "#888",
                          fontWeight: "600",
                          marginBottom: "16px"
                        }}>
                          Featured: {project.isFeatured ? 'Yes' : 'No'}
                        </p>
                        <div style={{
                          display: "flex",
                          gap: "8px",
                          marginTop: "8px",
                          flexWrap: "wrap"
                        }}>
                          <button
                            onClick={() => handleEditProject(project)}
                            disabled={projectsLoading}
                            style={{
                              flex: "1 1 auto",
                              padding: "8px 0",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                              background: projectsLoading ? "#ccc" : "#fff",
                              color: projectsLoading ? "#666" : "#181618",
                              border: "1px solid #baa170",
                              borderRadius: "6px",
                              cursor: projectsLoading ? "not-allowed" : "pointer",
                              transition: "all 0.2s"
                            }}
                          >
                            {projectsLoading ? "Loading..." : "Edit"}
                          </button>
                          {project.status === 'Draft' && (
                            <button
                              onClick={() => handlePublishProject(project.id)}
                              disabled={projectsLoading}
                              style={{
                                flex: "1 1 auto",
                                padding: "8px 0",
                                fontSize: "0.9rem",
                                fontWeight: "500",
                                background: projectsLoading ? "#ccc" : "#fff",
                                color: projectsLoading ? "#666" : "#181618",
                                border: "1px solid #baa170",
                                borderRadius: "6px",
                                cursor: projectsLoading ? "not-allowed" : "pointer",
                                transition: "all 0.2s"
                              }}
                            >
                              {projectsLoading ? "Loading..." : "Publish"}
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteProject(project.id)}
                            disabled={projectsLoading}
                            style={{
                              flex: "1 1 auto",
                              padding: "8px 0",
                              fontSize: "0.9rem",
                              fontWeight: "500",
                              background: "transparent",
                              color: projectsLoading ? "#999" : "#f44336",
                              border: `1px solid ${projectsLoading ? "#999" : "#f44336"}`,
                              borderRadius: "6px",
                              cursor: projectsLoading ? "not-allowed" : "pointer",
                              transition: "all 0.2s"
                            }}
                          >
                            {projectsLoading ? "Loading..." : "Delete"}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeMode === 'preview' && (
            <div>
              {renderPreview()}
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <button 
                  type="button"
                  onClick={() => setActiveMode('edit')}
                  style={{ 
                    background: "#fff", 
                    color: "#181618", 
                    border: "1px solid #baa170", 
                    borderRadius: 8, 
                    padding: "16px 32px", 
                    fontSize: "1.12rem", 
                    fontWeight: 400, 
                    cursor: "pointer", 
                    marginRight: 16,
                  }}
                >
                  Back to Edit
                </button>
                <button 
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ 
                    background: isSubmitting ? "#fff" : "#fff",
                    color: "#181618", 
                    border: "1px solid #baa170", 
                    borderRadius: 8, 
                    padding: "16px 32px", 
                    fontSize: "1.12rem", 
                    fontWeight: 500, 
                    cursor: isSubmitting ? "not-allowed" : "pointer", 
                    
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Project"}
                </button>
              </div>
            </div>
          )}

          {activeMode === 'edit' && (
        <form className="add-project-form" onSubmit={handleSubmit} style={{ maxWidth: 650, margin: "0 auto", background: "#fff", padding: 40, borderRadius: 18, border: "1px solid #baa170" }}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: 18, color: "#baa170" }}>Project Details</h2>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Project Name
            <input type="text" name="name" value={form.name} onChange={handleChange} required style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem", transition: "box-shadow 0.2s" }} />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Type
            <select name="type" value={form.type} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }}>
              <option value="commercial">Commercial</option>
              <option value="residential">Residential</option>
            </select>
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Description
            <textarea name="description" value={form.description} onChange={handleChange} required rows={4} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Location
            <input type="text" name="location" value={form.location} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Year/Date
            <input type="text" name="year" value={form.year} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Status
            <select name="status" value={form.status} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }}>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="Upcoming">Upcoming</option>
              <option value="Draft">Draft</option>
            </select>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, color: "#baa170", cursor: "pointer" }}>
            <input
              type="checkbox"
              name="isFeatured"
              checked={!!form.isFeatured}
              onChange={handleChange}
              style={{ width: 16, height: 16, accentColor: "#baa170" }}
            />
            Feature this project on home page
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Spacing
            <input type="text" name="spacing" value={form.spacing} onChange={handleChange} placeholder="e.g. 1200 sq ft" style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
        </div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#baa170" }}>Team & Recognition</h2>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Team
            <input type="text" name="team" value={form.team} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Client
            <input type="text" name="client" value={form.client} onChange={handleChange} style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} />
          </label>
        </div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#baa170" }}>Client Testimonial</h2>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Testimonial Text
            <textarea 
              name="testimonialText" 
              value={form.testimonialText} 
              onChange={handleChange} 
              placeholder="Enter client testimonial..."
              rows={4}
              style={{ 
                width: "100%", 
                padding: 10, 
                marginTop: 6, 
                borderRadius: 8, 
                border: "1px solid #baa170", 
                background: "#fff", 
                color: "#181816", 
                fontSize: "1rem",
                resize: "vertical",
                fontFamily: "inherit"
              }} 
            />
          </label>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Testimonial Author
            <input 
              type="text" 
              name="testimonialAuthor" 
              value={form.testimonialAuthor} 
              onChange={handleChange} 
              placeholder="e.g., John Smith @ Company Name"
              style={{ width: "100%", padding: 10, marginTop: 6, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", fontSize: "1rem" }} 
            />
          </label>
        </div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 18, color: "#baa170" }}>Images</h2>
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Main Image *
            <input type="file" accept="image/*" onChange={handleMainImageChange} required={!editingProjectId && !form.mainImagePreview} style={{ marginTop: 6, color: "#181816", fontSize: "1rem" }} />
          </label>
          {form.mainImagePreview && (
            <img src={form.mainImagePreview} alt="Main Preview" style={{ width: "100%", borderRadius: 8, marginBottom: 18, boxShadow: "0 2px 12px rgba(0,0,0,0.18)" }} />
          )}
          <label style={{ display: "block", marginBottom: 14, color: "#baa170" }}>
            Additional Images
            <input type="file" accept="image/*" multiple onChange={handleImagesChange} style={{ marginTop: 6, color: "#181816", fontSize: "1rem" }} />
          </label>
          {form.imagesPreview.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ marginBottom: 12, color: "#baa170", fontSize: "0.9rem", fontWeight: 600 }}>
                {form.imagesPreview.length} Image(s) - Drag to reorder or use buttons below
              </div>
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 18, padding: 12, background: "#f9f9f9", borderRadius: 8, border: "1px dashed #baa170" }}>
                {form.imagesPreview.map((src, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(idx)}
                    onDragEnd={handleDragEnd}
                    style={{
                      position: "relative",
                      display: "inline-block",
                      opacity: draggedImageIndex === idx ? 0.5 : 1,
                      cursor: "move",
                      transition: "opacity 0.2s"
                    }}
                  >
                    <img
                      src={src}
                      alt={`Preview ${idx + 1}`}
                      style={{
                        width: 110,
                        height: 85,
                        objectFit: "cover",
                        borderRadius: 8,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
                        border: draggedImageIndex === idx ? "2px solid #baa170" : "2px solid transparent"
                      }}
                    />
                    <div style={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 4
                    }}>
                      <button
                        type="button"
                        onClick={() => moveImageUp(idx)}
                        disabled={idx === 0}
                        title="Move up"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: idx === 0 ? "#ccc" : "#baa170",
                          color: "#fff",
                          border: "none",
                          cursor: idx === 0 ? "not-allowed" : "pointer",
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold"
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveImageDown(idx)}
                        disabled={idx === form.imagesPreview.length - 1}
                        title="Move down"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: idx === form.imagesPreview.length - 1 ? "#ccc" : "#baa170",
                          color: "#fff",
                          border: "none",
                          cursor: idx === form.imagesPreview.length - 1 ? "not-allowed" : "pointer",
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold"
                        }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        title="Remove image"
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          background: "#f44336",
                          color: "#fff",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: "bold"
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div style={{
                      position: "absolute",
                      bottom: -8,
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "#baa170",
                      color: "#fff",
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: "0.7rem",
                      fontWeight: "bold"
                    }}>
                      {idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: "1.18rem", fontWeight: 600, marginBottom: 10, color: "#baa170" }}>Content Layout Blocks</h2>
          <p style={{ marginTop: 0, marginBottom: 14, color: "#6e5b35", fontSize: "0.92rem", lineHeight: 1.5 }}>
            Add any number of blocks and arrange them in any order. Existing projects can be manually edited block-by-block. If no blocks are added, preview uses Description + Gallery fallback.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <button
              type="button"
              onClick={() => addContentLayoutBlock('text')}
              style={{ background: "#fff", color: "#181816", border: "1px solid #baa170", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 600 }}
            >
              + Text Block
            </button>
            <button
              type="button"
              onClick={() => addContentLayoutBlock('imageText')}
              style={{ background: "#fff", color: "#181816", border: "1px solid #baa170", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 600 }}
            >
              + Image + Text
            </button>
            <button
              type="button"
              onClick={() => addContentLayoutBlock('image')}
              style={{ background: "#fff", color: "#181816", border: "1px solid #baa170", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 600 }}
            >
              + Image Block
            </button>
            <button
              type="button"
              onClick={() => addContentLayoutBlock('gallery')}
              style={{ background: "#fff", color: "#181816", border: "1px solid #baa170", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontWeight: 600 }}
            >
              + Gallery Block
            </button>
          </div>

          {sanitizeContentBlocks(form.contentBlocks).length === 0 ? (
            <div style={{ border: "1px dashed #baa170", borderRadius: 8, padding: 12, color: "#6e5b35", fontSize: "0.9rem" }}>
              No custom blocks yet. Add a block to start custom placement.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {sanitizeContentBlocks(form.contentBlocks).map((block, blockIndex) => (
                <div key={block.id} style={{ border: "1px solid #e7dcc6", borderRadius: 10, padding: 14, background: "#fffaf0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{ color: "#6e5b35", fontWeight: 700, fontSize: "0.9rem" }}>
                      Block {blockIndex + 1} - {block.type}
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        type="button"
                        onClick={() => moveContentBlock(blockIndex, 'up')}
                        disabled={blockIndex === 0}
                        style={{ width: 28, height: 28, border: "1px solid #baa170", borderRadius: 6, background: blockIndex === 0 ? "#f0f0f0" : "#fff", cursor: blockIndex === 0 ? "not-allowed" : "pointer" }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveContentBlock(blockIndex, 'down')}
                        disabled={blockIndex === sanitizeContentBlocks(form.contentBlocks).length - 1}
                        style={{ width: 28, height: 28, border: "1px solid #baa170", borderRadius: 6, background: blockIndex === sanitizeContentBlocks(form.contentBlocks).length - 1 ? "#f0f0f0" : "#fff", cursor: blockIndex === sanitizeContentBlocks(form.contentBlocks).length - 1 ? "not-allowed" : "pointer" }}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeContentBlock(block.id)}
                        style={{ width: 28, height: 28, border: "1px solid #f44336", color: "#f44336", borderRadius: 6, background: "#fff", cursor: "pointer" }}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {block.type === 'text' && (
                    <>
                      <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                        Text Content
                        <textarea
                          rows={4}
                          value={block.text || ''}
                          onChange={(e) => updateContentBlock(block.id, { text: e.target.value })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", resize: "vertical" }}
                        />
                      </label>
                      <label style={{ display: "block", marginTop: 10, color: "#6e5b35", fontSize: "0.9rem" }}>
                        Sequence
                        <select
                          value={block.sequence || 'after-details'}
                          onChange={(e) => updateContentBlock(block.id, { sequence: e.target.value })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                        >
                          <option value="before-details">Before Project Details</option>
                          <option value="after-details">After Project Details</option>
                          <option value="after-images">After Detail Images</option>
                        </select>
                      </label>
                    </>
                  )}

                  {block.type === 'imageText' && (
                    <>
                      <label style={{ display: "block", marginBottom: 10, color: "#6e5b35", fontSize: "0.9rem" }}>
                        Text Content
                        <textarea
                          rows={4}
                          value={block.text || ''}
                          onChange={(e) => updateContentBlock(block.id, { text: e.target.value })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816", resize: "vertical" }}
                        />
                      </label>
                      <div style={{ display: "grid", gridTemplateColumns: windowWidth <= 860 ? "1fr" : "1fr 1fr 1fr", gap: 10 }}>
                        <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                          Placement
                          <select
                            value={block.placement || 'text-left'}
                            onChange={(e) => updateContentBlock(block.id, { placement: e.target.value })}
                            style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                          >
                            <option value="text-left">Text Left / Image Right</option>
                            <option value="image-left">Image Left / Text Right</option>
                            <option value="stacked">Stacked (Poster Style)</option>
                          </select>
                        </label>
                        <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                          Sequence
                          <select
                            value={block.sequence || 'after-details'}
                            onChange={(e) => updateContentBlock(block.id, { sequence: e.target.value })}
                            style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                          >
                            <option value="before-details">Before Project Details</option>
                            <option value="after-details">After Project Details</option>
                            <option value="after-images">After Detail Images</option>
                          </select>
                        </label>
                        <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                          Image
                          <select
                            value={block.imageIndex === null ? '' : block.imageIndex}
                            onChange={(e) => updateContentBlock(block.id, { imageIndex: e.target.value === '' ? null : Number(e.target.value) })}
                            style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                          >
                            <option value="">Select image</option>
                            {form.imagesPreview.map((_, imageIndex) => (
                              <option key={`imageText-${block.id}-${imageIndex}`} value={imageIndex}>
                                Image {imageIndex + 1}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </>
                  )}

                  {block.type === 'image' && (
                    <div style={{ display: "grid", gridTemplateColumns: windowWidth <= 680 ? "1fr" : "1fr 1fr", gap: 10 }}>
                      <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                        Sequence
                        <select
                          value={block.sequence || 'after-details'}
                          onChange={(e) => updateContentBlock(block.id, { sequence: e.target.value })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                        >
                          <option value="before-details">Before Project Details</option>
                          <option value="after-details">After Project Details</option>
                          <option value="after-images">After Detail Images</option>
                        </select>
                      </label>
                      <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                        Image
                        <select
                          value={block.imageIndex === null ? '' : block.imageIndex}
                          onChange={(e) => updateContentBlock(block.id, { imageIndex: e.target.value === '' ? null : Number(e.target.value) })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                        >
                          <option value="">Select image</option>
                          {form.imagesPreview.map((_, imageIndex) => (
                            <option key={`image-${block.id}-${imageIndex}`} value={imageIndex}>
                              Image {imageIndex + 1}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}

                  {block.type === 'gallery' && (
                    <>
                      <label style={{ display: "block", marginBottom: 10, color: "#6e5b35", fontSize: "0.9rem" }}>
                        Sequence
                        <select
                          value={block.sequence || 'after-details'}
                          onChange={(e) => updateContentBlock(block.id, { sequence: e.target.value })}
                          style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                        >
                          <option value="before-details">Before Project Details</option>
                          <option value="after-details">After Project Details</option>
                          <option value="after-images">After Detail Images</option>
                        </select>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#6e5b35", fontSize: "0.9rem", marginBottom: 8 }}>
                        <input
                          type="checkbox"
                          checked={block.useAllImages !== false}
                          onChange={(e) => updateContentBlock(block.id, { useAllImages: e.target.checked })}
                        />
                        Use all additional images
                      </label>
                      {block.useAllImages === false && (
                        <label style={{ display: "block", color: "#6e5b35", fontSize: "0.9rem" }}>
                          Image Indexes (comma separated, example: 0,1,3)
                          <input
                            type="text"
                            value={(block.imageIndexes || []).join(',')}
                            onChange={(e) => updateGalleryIndexes(block.id, e.target.value)}
                            style={{ width: "100%", marginTop: 6, padding: 10, borderRadius: 8, border: "1px solid #baa170", background: "#fff", color: "#181816" }}
                          />
                        </label>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 16, marginTop: 10 }}>
          <button 
            type="button" 
            onClick={() => handleSaveAsDraft()}
            disabled={isSubmitting}
            style={{ 
              flex: 1,
              background: isSubmitting ? "#ccc" : "#baa170", 
              color: "#fff", 
              border: "none", 
              borderRadius: 8, 
              padding: "16px 0", 
              fontSize: "1.12rem", 
              fontWeight: 600, 
              cursor: isSubmitting ? "not-allowed" : "pointer", 
              boxShadow: "0 2px 12px rgba(0,0,0,0.10)", 
              letterSpacing: 0.5, 
              transition: "background 0.2s" 
            }}
          >
            {isSubmitting ? "Saving..." : "Save as Draft"}
          </button>
          <button 
            type="submit" 
            disabled={isSubmitting}
            style={{ 
              flex: 1,
              background: isSubmitting ? "#ccc" : "#baa170", 
              color: "#fff", 
              border: "none", 
              borderRadius: 8, 
              padding: "16px 0", 
              fontSize: "1.12rem", 
              fontWeight: 600, 
              cursor: isSubmitting ? "not-allowed" : "pointer", 
              boxShadow: "0 2px 12px rgba(0,0,0,0.10)", 
              letterSpacing: 0.5, 
              transition: "background 0.2s" 
            }}
          >
            {isSubmitting ? "Submitting..." : editingProjectId ? "Update Project" : "Submit Project"}
          </button>
        </div>
        </form>
          )}
        </>
      )}
    </div>
  );
}
